/**
 * Twilio Region Types
 *
 * Centralized type definitions for Twilio regional infrastructure.
 * Single source of truth for all region-related types and constants.
 */

import { z } from "zod";

/**
 * Twilio Region Enum
 *
 * Supported Twilio regions for global infrastructure.
 * Each region has its own edge location and API endpoint.
 */
export const TwilioRegion = {
  US1: "us1", // United States (Ashburn, Virginia)
  IE1: "ie1", // Europe (Dublin, Ireland)
  AU1: "au1", // Australia (Sydney) - Future
  JP1: "jp1", // Japan (Tokyo) - Future
  SG1: "sg1", // Singapore - Future
  BR1: "br1" // Brazil (São Paulo) - Future
} as const;

export type TwilioRegion = (typeof TwilioRegion)[keyof typeof TwilioRegion];

/**
 * Currently Supported Regions (Active)
 *
 * Subset of TwilioRegion that are actually configured and operational.
 *
 * 🔴 THE FOUR "FUTURE" REGIONS ARE UNREACHABLE BY CONSTRUCTION, AND WIDENING
 * THIS ARRAY IS THE ONLY SWITCH THAT CHANGES THAT. Adding a value to
 * `TwilioRegion` or to `TWILIO_REGION_CONFIG` does nothing on its own: every
 * schema that validates a region on a WRITE path is
 * `SupportedTwilioRegionSchema`, which is this array. Verified by enumerating
 * the consumers, not inferred — there is no six-value region schema in the tree.
 *
 * 🚨 AND A STORED REGION OUTSIDE THIS SET IS NOT A DEGRADED READ, IT IS A DEAD
 * CONNECTION. `TwilioCredentialsSchema.region` is this schema, and
 * `extractTwilioCredentials` returns `null` for the WHOLE blob when any field
 * fails — so one unsupported region string refuses four doors at once: the
 * chat-assistant Twilio handler, Twilio voice dispatch, the in-call `sendSMS`
 * tool, and the voice-relay Twilio client. `decideTwilioAccount` treats
 * "configured but unreadable" as a refusal rather than falling through to the
 * platform master account, which is correct and is why the blast radius is a
 * refusal rather than a wrong-account call.
 *
 * So a region only becomes usable when it is added HERE and every stored blob
 * that already carries it can still be opened. Measured against production
 * 2026-08-20: 28 of 28 Twilio connections carry `region: "us1"`, staging 2 of 2
 * the same, so nothing is in the refused state today. (NEX-4245.)
 */
export const SUPPORTED_TWILIO_REGIONS = [TwilioRegion.US1, TwilioRegion.IE1] as const;

export type SupportedTwilioRegion = (typeof SUPPORTED_TWILIO_REGIONS)[number];

/**
 * Supported Regions Zod Schema
 *
 * Only accepts currently operational regions. Derived from
 * `SUPPORTED_TWILIO_REGIONS` so the array and the validator cannot disagree —
 * two hand-written lists of the same set is one of them going stale.
 */
export const SupportedTwilioRegionSchema = z.enum(SUPPORTED_TWILIO_REGIONS);

/**
 * Twilio Region Configuration
 *
 * Maps region codes to their infrastructure details.
 */
export interface TwilioRegionConfig {
  code: TwilioRegion;
  name: string;
  edge: string; // Twilio edge location
  location: string; // Geographic location
  available: boolean; // Is this region operational?
}

export const TWILIO_REGION_CONFIG: Record<TwilioRegion, TwilioRegionConfig> = {
  [TwilioRegion.US1]: {
    code: TwilioRegion.US1,
    name: "United States",
    edge: "ashburn",
    location: "Virginia, USA",
    available: true
  },
  [TwilioRegion.IE1]: {
    code: TwilioRegion.IE1,
    name: "Europe",
    edge: "dublin",
    location: "Dublin, Ireland",
    available: true
  },
  [TwilioRegion.AU1]: {
    code: TwilioRegion.AU1,
    name: "Australia",
    edge: "sydney",
    location: "Sydney, Australia",
    available: false // Future
  },
  [TwilioRegion.JP1]: {
    code: TwilioRegion.JP1,
    name: "Japan",
    edge: "tokyo",
    location: "Tokyo, Japan",
    available: false // Future
  },
  [TwilioRegion.SG1]: {
    code: TwilioRegion.SG1,
    name: "Singapore",
    edge: "singapore",
    location: "Singapore",
    available: false // Future
  },
  [TwilioRegion.BR1]: {
    code: TwilioRegion.BR1,
    name: "Brazil",
    edge: "sao-paulo",
    location: "São Paulo, Brazil",
    available: false // Future
  }
};

/**
 * Default region for backwards compatibility
 */
export const DEFAULT_TWILIO_REGION: SupportedTwilioRegion = TwilioRegion.US1;

/**
 * Helper: Get region config
 */
export function getTwilioRegionConfig(region: TwilioRegion): TwilioRegionConfig {
  return TWILIO_REGION_CONFIG[region];
}

/**
 * Helper: Check if region is supported
 */
export function isRegionSupported(region: TwilioRegion): region is SupportedTwilioRegion {
  const config = TWILIO_REGION_CONFIG[region];
  return config.available;
}

/**
 * Helper: Get available regions
 */
export function getAvailableRegions(): TwilioRegionConfig[] {
  return Object.values(TWILIO_REGION_CONFIG).filter((config) => config.available);
}

/**
 * Suggested Twilio Region for Country
 *
 * Maps ISO country codes to their SUGGESTED Twilio region for optimal API latency.
 * This is a HINT, not a requirement - phones are global resources.
 *
 * Any Twilio connection can purchase phones from any country.
 * Region only affects:
 * - API endpoint routing (latency optimization)
 * - Incoming call/SMS processing location
 *
 * @see https://www.twilio.com/docs/global-infrastructure/edge-locations
 */
export const SUGGESTED_REGION_FOR_COUNTRY: Record<string, TwilioRegion> = {
  // United States
  US: TwilioRegion.US1,
  USA: TwilioRegion.US1,

  // Europe (IE1 - Dublin)
  IE: TwilioRegion.IE1, // Ireland
  GB: TwilioRegion.IE1, // United Kingdom
  UK: TwilioRegion.IE1, // United Kingdom (alternate)
  DE: TwilioRegion.IE1, // Germany
  FR: TwilioRegion.IE1, // France
  IT: TwilioRegion.IE1, // Italy
  ES: TwilioRegion.IE1, // Spain
  NL: TwilioRegion.IE1, // Netherlands
  BE: TwilioRegion.IE1, // Belgium
  AT: TwilioRegion.IE1, // Austria
  CH: TwilioRegion.IE1, // Switzerland
  SE: TwilioRegion.IE1, // Sweden
  NO: TwilioRegion.IE1, // Norway
  DK: TwilioRegion.IE1, // Denmark
  FI: TwilioRegion.IE1, // Finland
  PL: TwilioRegion.IE1, // Poland
  CZ: TwilioRegion.IE1, // Czech Republic
  PT: TwilioRegion.IE1, // Portugal
  GR: TwilioRegion.IE1, // Greece

  // Australia/Pacific (AU1 - Sydney) - Future
  AU: TwilioRegion.AU1, // Australia
  NZ: TwilioRegion.AU1, // New Zealand

  // Asia (JP1/SG1) - Future
  JP: TwilioRegion.JP1, // Japan
  SG: TwilioRegion.SG1, // Singapore
  HK: TwilioRegion.SG1, // Hong Kong
  TH: TwilioRegion.SG1, // Thailand
  MY: TwilioRegion.SG1, // Malaysia
  PH: TwilioRegion.SG1, // Philippines
  ID: TwilioRegion.SG1, // Indonesia
  VN: TwilioRegion.SG1, // Vietnam
  IN: TwilioRegion.SG1, // India
  KR: TwilioRegion.JP1, // South Korea

  // Middle East (SG1 for now, might get dedicated region) - Future
  AE: TwilioRegion.SG1, // UAE
  SA: TwilioRegion.SG1, // Saudi Arabia
  QA: TwilioRegion.SG1, // Qatar
  KW: TwilioRegion.SG1, // Kuwait
  BH: TwilioRegion.SG1, // Bahrain
  OM: TwilioRegion.SG1, // Oman
  IL: TwilioRegion.IE1, // Israel (closer to EU)

  // South America (BR1 - São Paulo) - Future
  BR: TwilioRegion.BR1, // Brazil
  AR: TwilioRegion.BR1, // Argentina
  CL: TwilioRegion.BR1, // Chile
  CO: TwilioRegion.BR1, // Colombia
  PE: TwilioRegion.BR1, // Peru
  UY: TwilioRegion.BR1, // Uruguay

  // Canada (US1 - geographically close)
  CA: TwilioRegion.US1, // Canada

  // Mexico (US1 - geographically close)
  MX: TwilioRegion.US1 // Mexico
};

/**
 * Helper: Get suggested Twilio region for a country code
 *
 * Returns the SUGGESTED region for API routing (latency optimization).
 * This is a HINT, not a requirement - any connection can buy any country's phone.
 *
 * @param countryCode - ISO country code (e.g., "US", "IE", "AE")
 * @returns Suggested Twilio region for optimal API performance
 */
export function getSuggestedRegionForCountry(countryCode: string): TwilioRegion {
  const normalizedCode = countryCode.toUpperCase();
  const region = SUGGESTED_REGION_FOR_COUNTRY[normalizedCode];

  if (!region) {
    // Fallback to US for unknown countries (safe default)
    // eslint-disable-next-line no-console -- intentional diagnostic hint for an unmapped country code
    console.warn(
      `Unknown country code "${countryCode}", defaulting to ${TwilioRegion.US1}. ` +
        `Add to SUGGESTED_REGION_FOR_COUNTRY for optimal routing.`
    );
    return TwilioRegion.US1;
  }

  return region;
}

/**
 * @deprecated Use getSuggestedRegionForCountry() instead
 * Kept for backwards compatibility during the region refactor
 */
export function getRegionForCountry(countryCode: string): TwilioRegion {
  return getSuggestedRegionForCountry(countryCode);
}

/**
 * Helper: Check if country is supported (has available region)
 *
 * @param countryCode - ISO country code
 * @returns True if country's region is currently operational
 */
export function isCountrySupported(countryCode: string): boolean {
  const region = getRegionForCountry(countryCode);
  return isRegionSupported(region);
}
